import React, { useState } from "react";
import PropTypes from "prop-types";

const GenericTable = ({
  data,
  columns,
  onEdit,
  onDelete,
  onAdd,
  onView,
  additionalActions,
  checkboxSelection = true,
  customRowActions,
  onRowClick,
  selectedRows,
  onSelectRow,
  onSelectAllRows,
  isLoading = false,
  emptyStateMessage = "No data available",
  rowClassName,
  tableClassName = "table card-table table-vcenter text-nowrap datatable table-hover",
  uniqueId = "default",
  sortConfig = { key: null, direction: "asc" },
  onSort,
  isEditDisabled,
  isDeleteDisabled,
  visibleColumns,
  isRowSelectable,
  expandableRows = false,
  renderExpandedRow,
}) => {
  // Filter columns based on visibility
  const displayColumns = visibleColumns
    ? columns.filter((col) => visibleColumns.includes(col.key))
    : columns;

  const [expandedRow, setExpandedRow] = useState(null);

  const handleSort = (key) => {
    if (onSort) {
      onSort(key);
    }
  };

  const renderSortIcon = (key) => {
    const isActive = sortConfig.key === key;
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`sort-icon ms-2 ${isActive ? "text-primary" : "text-muted"}`}
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          transform:
            isActive && sortConfig.direction === "desc"
              ? "rotate(180deg)"
              : "rotate(0deg)",
          transition: "color 0.3s ease",
        }}
        aria-hidden="true"
        role="img"
        aria-label={`Sort ${isActive ? (sortConfig.direction === "desc" ? "descending" : "ascending") : "neutral"}`}
      >
        <path d="M7 15l5-5 5 5" />
      </svg>
    );
  };

  const handleRowClick = (item, e) => {
    if (
      e.target.closest(".btn-list") ||
      e.target.closest(".form-check-input") ||
      e.target.closest("a")
    ) {
      return;
    }
    onRowClick?.(item);
  };

  if (isLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="text-center p-4 text-muted">{emptyStateMessage}</div>
    );
  }

  return (
    <div className="table-responsive mt-3">
      <table className={tableClassName}>
        <thead>
          <tr>
            {expandableRows && <th width="40"></th>}
            {checkboxSelection && (
              <th className="w-1">
                <input
                  className="form-check-input m-0 align-middle"
                  type="checkbox"
                  id={`select-all-rows-${uniqueId}`}
                  name="selectAllRows"
                  checked={
                    data.length > 0 &&
                    selectedRows?.length > 0 &&
                    data
                      .filter(
                        (item) => !isRowSelectable || isRowSelectable(item),
                      )
                      .every((item) => selectedRows.includes(item.id))
                  }
                  onChange={(e) => {
                    if (onSelectAllRows) {
                      if (e.target.checked) {
                        // Select all selectable items
                        const allIds = data
                          .filter(
                            (item) => !isRowSelectable || isRowSelectable(item),
                          )
                          .map((item) => item.id);
                        onSelectAllRows(allIds);
                      } else {
                        // Deselect all
                        onSelectAllRows([]);
                      }
                    }
                  }}
                  autoComplete="off"
                />
              </th>
            )}
            {displayColumns.map((column) => (
              <th
                key={column.key}
                className={`cursor-pointer ${column.headerClassName || ""}`}
                onClick={() => handleSort(column.key)}
                data-sort={column.key}
                style={column.width ? { width: column.width } : {}}
                scope="col"
                role={column.sortable !== false ? "button" : undefined}
                tabIndex={column.sortable !== false ? 0 : undefined}
                aria-label={
                  column.sortable !== false
                    ? `Sort by ${column.label}`
                    : undefined
                }
                onKeyDown={
                  column.sortable !== false
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSort(column.key);
                        }
                      }
                    : undefined
                }
              >
                {column.label}{" "}
                {column.sortable !== false && renderSortIcon(column.key)}
              </th>
            ))}
            {(onEdit ||
              onDelete ||
              onAdd ||
              onView ||
              additionalActions ||
              customRowActions) && <th className="w-1">Action</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const rowKey = item.id
              ? `row-${item.id}`
              : item.companyName && item.email
                ? `row-${item.companyName}-${item.email}`
                : JSON.stringify(item);

            return (
              <React.Fragment key={rowKey}>
                <tr
                  onClick={(e) => handleRowClick(item, e)}
                  className={`${rowClassName?.(item) || ""}`}
                >
                  {expandableRows && (
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedRow(
                            expandedRow === item.id ? null : item.id,
                          );
                        }}
                      >
                        {expandedRow === item.id ? "▼" : "▶"}
                      </button>
                    </td>
                  )}
                  {checkboxSelection &&
                    (!isRowSelectable || isRowSelectable(item)) && (
                      <td>
                        <input
                          className="form-check-input m-0 align-middle"
                          type="checkbox"
                          id={`select-row-${item.id || rowKey}-${uniqueId}`}
                          name={`selectRow_${item.id || rowKey}-${uniqueId}`}
                          checked={selectedRows?.includes(item.id)}
                          onChange={(e) =>
                            onSelectRow?.(item.id, e.target.checked)
                          }
                          autoComplete="off"
                        />
                      </td>
                    )}
                  {checkboxSelection &&
                    isRowSelectable &&
                    !isRowSelectable(item) && <td></td>}
                  {displayColumns.map((column) => (
                    <td key={column.key} className={column.className}>
                      {column.render
                        ? column.render(item[column.key], item)
                        : item[column.key]}
                    </td>
                  ))}
                  {(onEdit ||
                    onDelete ||
                    onAdd ||
                    onView ||
                    additionalActions ||
                    customRowActions) && (
                    <td>
                      <div className="btn-list flex-nowrap">
                        {customRowActions?.(item)}
                        {onView && (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              onView(item);
                            }}
                            title="View"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-eye"
                            >
                              <path
                                stroke="none"
                                d="M0 0h24v24H0z"
                                fill="none"
                              />
                              <path d="M12 12m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                              <path d="M22 12c-2.667 4.667 -6 7 -10 7s-7.333 -2.333 -10 -7c2.667 -4.667 6 -7 10 -7s7.333 2.333 10 7" />
                            </svg>
                          </a>
                        )}
                        {onEdit && (
                          <a
                            href="#"
                            title={
                              isEditDisabled && isEditDisabled(item)
                                ? item?.status === 2
                                  ? "Cannot edit: Proposal is already approved"
                                  : item?.statusProposal === 3
                                    ? "Cannot edit: Proposal is already accepted"
                                    : item?.projectStatus === "Converted"
                                      ? "Cannot edit: Proposal is already converted to project"
                                      : "This item cannot be edited"
                                : "Edit"
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isEditDisabled || !isEditDisabled(item)) {
                                onEdit(item);
                              }
                            }}
                            style={{
                              opacity:
                                isEditDisabled && isEditDisabled(item)
                                  ? 0.4
                                  : 1,
                              cursor:
                                isEditDisabled && isEditDisabled(item)
                                  ? "not-allowed"
                                  : "pointer",
                              pointerEvents:
                                isEditDisabled && isEditDisabled(item)
                                  ? "none"
                                  : "auto",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-edit"
                            >
                              <path
                                stroke="none"
                                d="M0 0h24v24H0z"
                                fill="none"
                              />
                              <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                              <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                              <path d="M16 5l3 3" />
                            </svg>
                          </a>
                        )}
                        {onDelete && (
                          <a
                            href="#"
                            title={
                              isDeleteDisabled && isDeleteDisabled(item)
                                ? item?.status === "PAID"
                                  ? "Cannot delete: Invoice is already paid"
                                  : item?.status === "FINAL"
                                    ? "Cannot delete: Invoice is finalized"
                                    : "This item cannot be deleted"
                                : "Delete"
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              if (
                                !isDeleteDisabled ||
                                !isDeleteDisabled(item)
                              ) {
                                onDelete(item);
                              }
                            }}
                            style={{
                              opacity:
                                isDeleteDisabled && isDeleteDisabled(item)
                                  ? 0.4
                                  : 1,
                              cursor:
                                isDeleteDisabled && isDeleteDisabled(item)
                                  ? "not-allowed"
                                  : "pointer",
                              pointerEvents:
                                isDeleteDisabled && isDeleteDisabled(item)
                                  ? "none"
                                  : "auto",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="red"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-trash"
                            >
                              <path
                                stroke="none"
                                d="M0 0h24v24H0z"
                                fill="none"
                              />
                              <path d="M4 7l16 0" />
                              <path d="M10 11l0 6" />
                              <path d="M14 11l0 6" />
                              <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                              <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                            </svg>
                          </a>
                        )}
                        {onAdd && (
                          <a
                            href="#"
                            title="Add"
                            onClick={(e) => {
                              e.preventDefault();
                              onAdd(item);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-plus"
                            >
                              <path
                                stroke="none"
                                d="M0 0h24v24H0z"
                                fill="none"
                              />
                              <path d="M12 5l0 14" />
                              <path d="M5 12l14 0" />
                            </svg>
                          </a>
                        )}
                        {additionalActions && additionalActions(item)}
                      </div>
                    </td>
                  )}
                </tr>

                {expandableRows &&
                  expandedRow === item.id &&
                  renderExpandedRow && (
                    <tr>
                      <td colSpan={999}>{renderExpandedRow(item)}</td>
                    </tr>
                  )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

GenericTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
      type: PropTypes.string,
      sortable: PropTypes.bool,
      width: PropTypes.string,
      className: PropTypes.string,
      headerClassName: PropTypes.string,
    }),
  ).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onAdd: PropTypes.func,
  onView: PropTypes.func,
  additionalActions: PropTypes.func,
  checkboxSelection: PropTypes.bool,
  customRowActions: PropTypes.func,
  onRowClick: PropTypes.func,
  selectedRows: PropTypes.array,
  onSelectRow: PropTypes.func,
  onSelectAllRows: PropTypes.func,
  isLoading: PropTypes.bool,
  emptyStateMessage: PropTypes.string,
  rowClassName: PropTypes.func,
  tableClassName: PropTypes.string,
  uniqueId: PropTypes.string,
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(["asc", "desc"]),
  }),
  onSort: PropTypes.func,
  isEditDisabled: PropTypes.func,
  isDeleteDisabled: PropTypes.func,
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  isRowSelectable: PropTypes.func,
};

export default GenericTable;
